import { isToday, isYesterday } from 'date-fns';
import type { TFunction } from 'i18next';
import { MessageCircle, MoreHorizontal, Pencil, Search, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@shared/components/ui/AlertDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/DropdownMenu';
import { cn } from '@shared/components/ui/cn';
import { useToastStore } from '@shared/stores/toast.store';

import { refreshAiCioHistory } from '../service/history-sync';
import { isBlankConversation, useConversationsStore } from '../store/conversations.store';
import type { Conversation } from '../types';

interface ChatHistoryProps {
  /** Called after a conversation is picked (e.g. to close the popover). */
  onSelect?: (id: string) => void;
  /** Show the search box at the top of the list. */
  showSearch?: boolean;
  className?: string;
}

interface Group {
  key: string;
  label: string;
  items: Conversation[];
}

function relativeTime(ts: number, t: TFunction<'ai-cio'>, language: string): string {
  const diffMin = Math.round((Date.now() - ts) / 60000);
  if (diffMin < 1) return t('history.justNow', 'just now');
  if (diffMin < 60) return t('history.minutesAgo', '{{count}} min ago', { count: diffMin });
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return t('history.hoursAgo', '{{count}} hr ago', { count: diffHr });
  return new Date(ts).toLocaleDateString(language, { month: 'short', day: 'numeric' });
}

/**
 * Recent-conversations list grouped by Today / Yesterday / Previous, used both
 * as the right-side history drawer on the full page and the in-panel history.
 * Each row supports rename (inline) and delete (confirmed) per the AI CIO spec;
 * a footer action clears all history (confirmed). Both wait for the server, since
 * they erase the saved chats there, and report a refusal or failure as a toast.
 */
export function ChatHistory({ onSelect, showSearch, className }: ChatHistoryProps) {
  const { t, i18n } = useTranslation('ai-cio');
  const conversations = useConversationsStore((s) => s.conversations);
  const order = useConversationsStore((s) => s.order);
  const activeId = useConversationsStore((s) => s.activeId);
  const selectConversation = useConversationsStore((s) => s.selectConversation);
  const renameConversation = useConversationsStore((s) => s.renameConversation);
  const deleteConversation = useConversationsStore((s) => s.deleteConversation);
  const clearHistory = useConversationsStore((s) => s.clearHistory);
  const addToast = useToastStore((s) => s.addToast);

  const [query, setQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Conversation | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  // The list is served from the server-side index: refresh it on every open.
  useEffect(() => {
    void refreshAiCioHistory();
  }, []);

  const groups = useMemo<Group[]>(() => {
    const q = query.trim().toLowerCase();
    const visible = order
      .map((id) => conversations[id])
      // Server-known conversations arrive as stubs with no messages loaded yet — they
      // must still be listed; only never-used local drafts are hidden.
      .filter((c): c is Conversation => c !== undefined && !isBlankConversation(c))
      .filter((c) => (q ? c.title.toLowerCase().includes(q) : true));

    const today: Conversation[] = [];
    const yesterday: Conversation[] = [];
    const previous: Conversation[] = [];
    for (const c of visible) {
      if (isToday(c.updatedAt)) today.push(c);
      else if (isYesterday(c.updatedAt)) yesterday.push(c);
      else previous.push(c);
    }

    return [
      { key: 'today', label: t('history.today', 'Today'), items: today },
      { key: 'yesterday', label: t('history.yesterday', 'Yesterday'), items: yesterday },
      { key: 'previous', label: t('history.previous', 'Previous'), items: previous },
    ].filter((g) => g.items.length > 0);
  }, [order, conversations, query, t]);

  function handleSelect(id: string) {
    selectConversation(id);
    onSelect?.(id);
  }

  function startRename(c: Conversation) {
    setEditingId(c.id);
    setDraftTitle(c.title);
  }

  function commitRename() {
    if (editingId) {
      const current = conversations[editingId];
      const next = draftTitle.trim();
      // Skip no-op renames so we don't issue a redundant history sync on blur.
      if (current && next && next !== current.title) renameConversation(editingId, next);
    }
    setEditingId(null);
  }

  async function confirmDelete(c: Conversation) {
    setDeleting(true);
    const outcome = await deleteConversation(c.id);
    setDeleting(false);
    setPendingDelete(null);
    if (outcome === 'busy') {
      addToast({
        variant: 'error',
        title: t(
          'history.deleteBusy',
          'This chat is still answering. Try again when the reply finishes.',
        ),
      });
    } else if (outcome === 'failed') {
      addToast({
        variant: 'error',
        title: t('history.deleteFailed', "Couldn't delete this chat. Try again."),
      });
    }
  }

  async function confirmClear() {
    setClearing(true);
    const outcome = await clearHistory();
    setClearing(false);
    setClearOpen(false);
    if (outcome === 'busy') {
      addToast({
        variant: 'error',
        title: t(
          'history.clearBusy',
          'A reply is still being written. Try again when it finishes.',
        ),
      });
    } else if (outcome === 'failed') {
      addToast({
        variant: 'error',
        title: t('history.clearFailed', "Couldn't clear your history. Try again."),
      });
    }
  }

  function onRenameKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingId(null);
    }
  }

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      {showSearch && (
        <div className="flex items-center gap-2 border-b px-3 py-2.5">
          <Search className="size-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            placeholder={t('history.search', 'Search chats...')}
            aria-label={t('history.search', 'Search chats...')}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {groups.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            {t('history.empty', 'No conversations yet')}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.key} className="mb-2">
              <p className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {group.label}
              </p>
              {group.items.map((c) => (
                <div
                  key={c.id}
                  className={cn(
                    'group/row relative flex items-center gap-2 rounded-md ps-2 pe-1 text-sm transition-colors hover:bg-accent',
                    c.id === activeId && 'bg-accent',
                  )}
                >
                  {editingId === c.id ? (
                    <>
                      <MessageCircle className="size-4 shrink-0 text-muted-foreground" />
                      <input
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                        value={draftTitle}
                        onChange={(e) => {
                          setDraftTitle(e.target.value);
                        }}
                        onKeyDown={onRenameKeyDown}
                        onBlur={commitRename}
                        aria-label={t('history.rename', 'Rename')}
                        className="my-1.5 min-w-0 flex-1 rounded-sm bg-background px-1.5 py-0.5 text-sm text-foreground outline-none ring-1 ring-primary/40"
                      />
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          handleSelect(c.id);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-2 py-2 text-start"
                      >
                        <MessageCircle className="size-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate text-foreground">{c.title}</span>
                      </button>
                      <span className="shrink-0 text-xs text-muted-foreground group-hover/row:hidden">
                        {relativeTime(c.updatedAt, t, i18n.language)}
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label={t('history.more', 'More options')}
                            className="hidden size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground group-hover/row:flex data-[state=open]:flex"
                          >
                            <MoreHorizontal className="size-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem
                            onSelect={() => {
                              startRename(c);
                            }}
                          >
                            <Pencil className="size-4" />
                            {t('history.rename', 'Rename')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={() => {
                              setPendingDelete(c);
                            }}
                          >
                            <Trash2 className="size-4" />
                            {t('history.delete', 'Delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="shrink-0 border-t p-2">
        <button
          type="button"
          onClick={() => {
            setClearOpen(true);
          }}
          className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-destructive transition-colors hover:bg-accent"
        >
          <Trash2 className="size-4" />
          {t('history.clearAll', 'Clear all history')}
        </button>
      </div>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('history.deleteTitle', 'Delete chat?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'history.deleteDescription',
                'This permanently deletes "{{title}}", its saved transcript and what AI CIO learned only from this chat. Preferences you saved to AI Memory are kept. This can\'t be undone.',
                { title: pendingDelete?.title ?? '' },
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t('history.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(e) => {
                // Keep the dialog open until the server has erased the chat.
                e.preventDefault();
                if (pendingDelete) void confirmDelete(pendingDelete);
              }}
            >
              {deleting ? t('history.deleting', 'Deleting…') : t('history.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={clearOpen}
        onOpenChange={(open) => {
          if (!open && !clearing) setClearOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('history.clearAllTitle', 'Clear all chat history?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'history.clearAllDescription',
                "This permanently deletes every saved AI CIO chat, chats you deleted earlier included, and everything AI CIO learned about you, AI Memory included. This can't be undone.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearing}>
              {t('history.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={clearing}
              onClick={(e) => {
                // Keep the dialog open until the server has cleared the history.
                e.preventDefault();
                void confirmClear();
              }}
            >
              {clearing
                ? t('history.clearing', 'Clearing…')
                : t('history.clearAllConfirm', 'Clear all')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
